import React from 'react'
import { CircularProgress, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material'
import { AppConfigPropsType, AppContainerProps, AppContainerState, ContactFieldGroup, StepPage } from './types'
import CloseIcon from '@mui/icons-material/Close'
import './AppContainer.css'
import QuizButton from './components/QuizButton'
import { createTheme, ThemeProvider, Theme } from '@mui/material/styles'
import { validateHexCode } from './utils'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { App } from './App'

const contactStorageKey = 'cyber411-rsa-contact'
const configSteps: StepPage[] = [
  {
    page: 'contact',
    label: 'Contact Info',
  },
  {
    page: 'quiz',
    label: 'Assessment',
  },
  {
    page: 'final',
    label: 'Download Report',
  },
]
const getConfigSteps = () => configSteps.map(step => ({ ...step, completed: false }))
const useColor = (color: string, defaultColor: string) => color && validateHexCode(color) ? color : defaultColor

class AppContainer extends React.Component<AppContainerProps, AppContainerState> {
  constructor(props: AppContainerProps) {
    super(props)
    this.state = {
      api_endpoint: this.props.api_endpoint || null,
      showModal: false,
      config: {} as AppConfigPropsType,
      loading: true,
      currentPage: 'contact',
      stepPages: getConfigSteps(),
      showContactValidationMessage: false,
      windowWidth: window.innerWidth,
      sentStatus: 'unsent',
      theme: {} as Theme
    }
  }

  componentDidMount = async (): Promise<any> => {
    let config = this.props.config || {} as AppConfigPropsType
    if (this.props.api_endpoint) {
      const response = await fetch(this.props.api_endpoint + '/')
      const data = await response.json()
      config = {
        CaptchaSiteKey: data.CaptchaSiteKey,
        ContactFieldGroups: data.ContactFieldGroups,
        Questions: data.Questions,
        DisplayAsModal: data.DisplayAsModal !== undefined ? data.DisplayAsModal : this.props.config.DisplayAsModal,
        AutoNext: data.AutoNext !== undefined ? data.AutoNext : this.props.config.AutoNext,
        Theme: data.Theme !== undefined ? data.Theme : this.props.config.Theme,
        ModalButtonText: data.ModalButtonText !== undefined ? data.ModalButtonText : this.props.config.ModalButtonText,
        Referral: data.Referral !== undefined ? data.Referral : this.props.config.Referral,
      }
    }
    window.addEventListener('resize', this.UpdateWindowWidth)
    this.setState({
      config: {
        ...config,
        ContactFieldGroups: this.InitContactFieldValues(config.ContactFieldGroups),
      },
      loading: false,
      theme: createTheme({
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                padding: '6px 16px',
                textTransform: 'none',
                fontWeight: 'normal',
              },
            },
          },
        },
        palette: {
          primary: {
            main: useColor(config.Theme.HighlightColor || '', '#006BB3'),
            contrastText: useColor(config.Theme.ContrastColor || '', '#fff'),
          },
        },
      }),
    })
  }

  UpdateWindowWidth = () => {
    this.setState({
      windowWidth: window.innerWidth,
    })
  }

  IsMobile = () => {
    return this.state.windowWidth <= 768
  }

  HandleShowModal = () => {
    this.setState({
      showModal: !this.state.loading,
    })
  }

  HandleHideModal = () => {
    this.setState({
      showModal: false,
    })
  }

  InitContactFieldValues = (fieldGroups: ContactFieldGroup[]): ContactFieldGroup[] => {
    const payload: ContactFieldGroup[] = JSON.parse(localStorage.getItem(contactStorageKey) || '[]') as ContactFieldGroup[]
    for (let group of fieldGroups) {
      const currGroup = payload.find(entry => entry.group_id === group.group_id)
      if (currGroup) {
        for (let field of group.fields) {
          const currField = currGroup.fields.find(cf => cf.name === field.name)
          if (currField) {
            field.value = currField.value
          }
        }
      }
    }
    return fieldGroups
  }

  render = () => {
    if (!this.state.config || !this.state.theme || this.state.loading) {
      return (
        <CircularProgress color="inherit" size={'2rem'} />
      )
    }
    return (
      <ThemeProvider theme={this.state.theme}>
        <div className={'cyber411-rsa-root'}>
          {this.state.config.DisplayAsModal ?
            <div>
              <QuizButton
                Action={this.HandleShowModal}
                label={this.state.config.ModalButtonText || "Get Started"}
                variant='contained'
                loading={this.state.loading}
              />
              <Dialog
                open={this.state.showModal}
                // onClose={this.HandleHideModal}
                closeAfterTransition={false} 
                className='cyber411-rsa-dialog'
                aria-label='Self-Assessment Modal'
                fullWidth
                maxWidth='sm'
                keepMounted
              >
                <DialogTitle
                  sx={{ padding: '1rem' }}
                >
                  Cyber Risk Self-Assessment
                </DialogTitle>
                <IconButton
                  aria-label="close"
                  onClick={this.HandleHideModal}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
                <DialogContent
                  sx={{ padding: '1rem' }}
                >
                  <GoogleReCaptchaProvider reCaptchaKey={this.state.config.CaptchaSiteKey}>
                    <App
                      {...this.state}
                      onHideModal={this.HandleHideModal}
                    />
                  </GoogleReCaptchaProvider>
                </DialogContent>
              </Dialog>
            </div> :
            <div style={{ padding: '1rem' }}>
              {this.state.loading ?
                <CircularProgress color="inherit" sx={{ margin: '1rem' }} /> :
                <>
                  <GoogleReCaptchaProvider reCaptchaKey={this.state.config.CaptchaSiteKey}>
                    <App
                      {...this.state}
                    />
                  </GoogleReCaptchaProvider>
                </>
              }
            </div>
          }
        </div>
      </ThemeProvider>
    )
  }
}

export default AppContainer
